import React from 'react';
import { Events, Logger } from '@elyx-code/common-ts-utils';
import { CanvasObject, Connection } from '../../lib/canvas';
import { ConnectionNodeType, EditorService } from '.';
import {
	CallableEntityState,
	CallerEntityState,
	ChangeSet,
	ProjectState,
	StateMutationAction,
	UserManagedEntityState,
	ValueReadingEntityState,
	ValueWritingEntityState
} from '@elyx-code/project-logic-tree';
import { createRoot, ReactRoot } from '../../utils/react';
import { getEntityIdFromCanvasId } from './ui';
import ConnectionDeleteButton, {
	IConnectionDeleteButtonProps
} from '../../components/draggable-entity-card/ConnectionDeleteButton';
import { fromSourceAndTargetEntityToConnectionNodeType } from './utils';

export async function disconnect(
	sourceEntity: UserManagedEntityState,
	targetEntity: UserManagedEntityState,
	connection: Connection,
	changeSet: ChangeSet
) {
	const connectionNodeType: ConnectionNodeType =
		fromSourceAndTargetEntityToConnectionNodeType(
			sourceEntity,
			targetEntity,
			connection.startId,
			connection.endId
		);

	let actionName = StateMutationAction.DisconnectNodes;

	if (connectionNodeType === ConnectionNodeType.CalledBy) {
		(targetEntity as CallableEntityState).removeCaller(
			sourceEntity as CallerEntityState,
			changeSet,
			true
		);

		(sourceEntity as CallerEntityState).removeCall(
			targetEntity as CallableEntityState,
			changeSet,
			true
		);

		actionName = StateMutationAction.DisconnectExecutions;
		changeSet.actionName = actionName;
	} else if (connectionNodeType === ConnectionNodeType.PropertyIn) {
		(targetEntity as ValueReadingEntityState).removeValueWriter(changeSet);

		(sourceEntity as ValueWritingEntityState).removeValueReader(
			targetEntity as ValueReadingEntityState,
			changeSet
		);

		actionName = StateMutationAction.DisconnectValues;
		changeSet.actionName = actionName;
	}
}

export class CanvasConnectionDeleteButtonElement extends Events {
	canvasObject!: CanvasObject;
	component!: React.FC<IConnectionDeleteButtonProps>;
	node!: ReactRoot;
	connection: Connection;

	// Parent canvas service
	project: EditorService;

	constructor(
		project: EditorService,
		config: {
			connection: Connection;
		} = { connection: null }
	) {
		super();
		this.project = project;
		this.connection = config.connection;
	}

	get ref() {
		return this.node.ref;
	}

	get ready() {
		return this.node.mounted;
	}

	async init(): Promise<CanvasConnectionDeleteButtonElement> {
		await this.render();

		this.canvasObject.onBeforeDelete(() => {
			this.canvasObject.element.remove();
		});

		return this;
	}

	async remove(): Promise<void> {
		await this.node.unmount();

		this.canvasObject.remove();
	}

	async render(): Promise<CanvasConnectionDeleteButtonElement> {
		if (!this.canvasObject) {
			const domElement = document.createElement('div');

			// Append to canvas element
			this.project.canvas?.canvasElement.appendChild(domElement);

			this.canvasObject = this.project.canvas?.object({
				id: 'delete-connection-button',
				// Create temporary div
				element: domElement,
				deletable: true,
				selectable: false,
				coordinates: {
					x: 0,
					y: 0
				}
			});
		}

		this.component = ConnectionDeleteButton;

		const sourceEntity = this.project.logic.get(
			getEntityIdFromCanvasId(this.connection.startId)
		) as UserManagedEntityState;

		const targetEntity = this.project.logic.get(
			getEntityIdFromCanvasId(this.connection.endId)
		) as UserManagedEntityState;

		const resolvedProps: IConnectionDeleteButtonProps = {
			project: this.project,
			canvasObject: this.canvasObject,
			connection: this.connection,
			sourceEntity: sourceEntity,
			targetEntity: targetEntity,
			onClick: (e) => {
				e.stopPropagation();
				const sourceEntity = this.project.logic.get(
					getEntityIdFromCanvasId(this.connection.startId)
				) as UserManagedEntityState;

				const targetEntity = this.project.logic.get(
					getEntityIdFromCanvasId(this.connection.endId)
				) as UserManagedEntityState;

				// Remove the connection
				this.connection.sourceParentObject.removeConnection(
					this.connection
				);

				const deleteConnectionChangeSet =
					this.project.logic.addChangeSet(
						new ChangeSet(
							this.project.logic,
							ProjectState.sessionAuthor,
							new Date().toISOString(),
							this.project.logic,
							false,
							StateMutationAction.DisconnectNodes
						)
					);

				disconnect(
					sourceEntity,
					targetEntity,
					this.connection,
					deleteConnectionChangeSet
				);

				// Remove delete connection button
				this.project.hideConnectionDeleteButtonElement();

				this.project.renderAndCloseChangeSet(deleteConnectionChangeSet);
			},
			tooltip:
				this.project.canvas?.selectedConnections.length > 1
					? 'Delete all selected connections'
					: 'Delete connection'
		};

		if (!this.node) {
			this.node = createRoot('#delete-connection-button');
		}

		await this.node.render(this.component, resolvedProps, null, {
			onError: (error) => {
				Logger.log('Error rendering connection delete button: ', error);
				this.project.events.emit('canvas-react-error', error);
			}
		});

		return this;
	}
}
